import type {
  Report,
  SessionSummary,
  VizFile,
  DepsHealth,
  EngineCapabilityManifest,
  EvolutionProposal,
  ResearchSessionManifest,
  UserRole,
} from "@re/shared";
import type { VercelAiModel } from "./models";

export type { EngineCapabilityManifest, EvolutionProposal, ResearchSessionManifest } from "@re/shared";

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, { credentials: "include", ...init });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`API ${res.status} ${path}: ${detail.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

export type RunnerKind = "claude" | "codex" | "vercel-ai";
export type RunnerStatus = "running" | "done" | "error" | "killed";

export interface JobMeta {
  id: string;
  target: string;
  status: RunnerStatus;
  startedAt: number;
  endedAt: number | null;
}

export interface RunnersInfo {
  disabled: boolean;
  claude: { available: boolean };
  codex:  { available: boolean };
  vercelAi: { available: boolean };
}

export interface RunnerEvent {
  seq: number;
  ts: number;
  stream: "stdout" | "stderr" | "system";
  text: string;
}

export interface MeResponse {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

export const api = {
  listSessions: () => http<{ sessions: SessionSummary[] }>("/sessions").then((r) => r.sessions),
  listPublicSessions: () =>
    http<{ sessions: SessionSummary[] }>("/sessions?scope=public").then((r) => r.sessions),
  listMySessions: () =>
    http<{ sessions: SessionSummary[] }>("/sessions?scope=mine").then((r) => r.sessions),
  me: () => http<MeResponse>("/me"),
  deleteSession: (slug: string) =>
    http<{ ok: boolean }>(`/sessions/${encodeURIComponent(slug)}`, { method: "DELETE" }),
  devUpgrade: () => http<{ ok: boolean; role: string }>("/me/dev-upgrade", { method: "POST" }),
  setUserRole: (id: string, role: string) =>
    http<{ ok: boolean }>(`/admin/users/${encodeURIComponent(id)}/role`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ role }),
    }),
  getReport: (slug: string) => http<Report>(`/sessions/${encodeURIComponent(slug)}`),
  getTranscript: (slug: string) =>
    http<{ markdown: string }>(`/sessions/${encodeURIComponent(slug)}/transcript`).then((r) => r.markdown),
  getViz: (slug: string) => http<VizFile>(`/sessions/${encodeURIComponent(slug)}/viz`),
  getDeps: () => http<DepsHealth>("/health/deps"),

  capabilities: () => http<EngineCapabilityManifest>("/research/capabilities"),
  getSessionManifest: (slug: string) =>
    http<ResearchSessionManifest>(`/sessions/${encodeURIComponent(slug)}/manifest`),
  listEvolutionProposals: () =>
    http<{ proposals: EvolutionProposal[] }>("/evolution/proposals").then((r) => r.proposals),

  runners: () => http<RunnersInfo>("/research/runners"),
  listJobs: () => http<{ jobs: JobMeta[] }>("/research").then((r) => r.jobs),
  startResearch: (body: {
    target: string;
    runner: RunnerKind | "both";
    model?: VercelAiModel;
    yes?: boolean;
    fresh?: boolean;
    slug?: string;
  }) =>
    http<{ jobs: { id: string; kind: RunnerKind }[]; errors: unknown[] }>("/research", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  getJob: (id: string) => http<JobMeta>(`/research/${encodeURIComponent(id)}`),
  killJob: (id: string) =>
    http<{ ok: boolean }>(`/research/${encodeURIComponent(id)}/kill`, { method: "POST" }),
};
